'use client';
/* External Imports */
import { Box, Text } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@app-ps/Components/Ui/LazyLoadImageComponent';
import { getEmbedUrl } from '@app-ps/Components/Utility/utils';
import config from '@config';
import { useEffect, useState } from 'react';

/**
 * @param {Object} sectionContent - Data from api
 * @param {Object} region - Data from api
 * @param {Object} lang - Data from api
 * @returns
 */

const AudioEmbed = ({ sectionContent, region, lang }) => {
  const [isAudioLoad, setAudioLoad] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handleAudioClick = () => {
    if (!isAudioLoad) {
      setAudioLoad(true);
    } else {
      setIsPlaying(prev => !prev);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && isAudioLoad && !audioPlayer) {
      const audio = new Audio(sectionContent.embedUrl);
      setAudioPlayer(audio);

      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });

      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });
    }
  }, [isAudioLoad, audioPlayer, sectionContent.embedUrl]);

  useEffect(() => {
    if (audioPlayer) {
      if (isPlaying) {
        audioPlayer.play();
      } else {
        audioPlayer.pause();
      }
    }
  }, [isPlaying, audioPlayer]);

  useEffect(() => {
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
      }
    };
  }, [audioPlayer]);

  const formatTime = time => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  return (
    <Box>
      {sectionContent?.source === 'soundcloud' && (
        <Box
          as="iframe"
          width="100%"
          allowFullScreen
          style={{
            // position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
          src={getEmbedUrl(sectionContent?.embedUrl)}
        />
      )}
      {sectionContent?.source !== 'soundcloud' && (
        <Box
          className="audio-player"
          width="100%"
          bgColor="#0E1216"
          height={{ xl: '100px' }}
          paddingY="1.5rem"
          opacity="100%"
          mt={{ base: '0' }}
        >
          <Box
            className="timeline"
            ml="1rem"
            bgColor="#979797"
            width={{
              base: '94px',
              sm: '50%',
              md: '70%',
              lg: '73%',
              xl: '487px',
            }}
            height={{ base: '8px' }}
          >
            <Box className="progress"> </Box>
          </Box>
          <Box className="controls" mt={{ base: '-4px', md: '-8px' }}>
            <Box className="play-container">
              <Box
                className="toggle-play play"
                onClick={handleAudioClick}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="#60ADFF"
                width="52px" // Adjust size as needed
                height="52px"
                borderRadius="50%"
              >
                {/* https://static.sadhguru.org/d/46272/31229860-play-icon-button.png */}
                {isPlaying ? (
                  <LazyLoadImageComponent
                    src="https://static.sadhguru.org/d/46272/31229860-pause-icon-button.png"
                    alt="Pause icon"
                    boxSize="2.5rem" // Adjust the size as needed
                  />
                ) : (
                  <LazyLoadImageComponent
                    src="https://static.sadhguru.org/d/46272/1731229860-play-icon-button.png"
                    alt="Pause icon"
                    boxSize="2.5rem" // Adjust the size as needed
                  />
                )}
              </Box>
            </Box>
            <Box className="time">
              <Box className="current"> {formatTime(currentTime)}</Box>
              <Text>/</Text>
              <Box className="length">{formatTime(duration)}</Box>
            </Box>
            <Box className="volume-container">
              <Box className="volume-button">
                <Box className="volume icono-volumeMedium"></Box>
              </Box>
              <Box className="volume-slider">
                <Box className="volume-percentage"></Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <style global jsx>
        {`
              .articlebody-arabic {
                direction: rtl;
              }
              .text-dir {
                direction: rtl;
                unicode-bidi: bidi-override;
              }
              strong {
                font-family: 'var(--FedraSansStd-medium)' !important;
                font-weight: normal !important;
              }
              hr {
                border-color: #eee;
              }
              table p {
                margin: 0 0 25px 0 !important;
              }
              td p {
                margin: 0 0 16px 0 !important;
              }
              .articlebody ol,
              .articlebody ul {
                padding-left: 1.5em;
              }
              .articlebody ul li,
              .articlebody ol li {
                padding: 3px 0.6vw;
              }

              .articlebody p iframe {
                width: 100%;
              }
              .articlebody blockquote {
                padding: 10px 20px;
                margin: 0 0 20px;
                font-size: 17.5px;
                border-left: 5px solid #eee;
              }

              .articlebody h2 {
                font-size: 30px;
                line-height: 1.1;
                // Need to look into this, commenting as of now
                // float: left;
                // width: 100%;
              }

              .articlebody h3 {
                font-size: 27px !important;
              }

              .articlebody h4 {
                font-size: 24px !important;
                line-height: 1.21;
              }

              .articlebody h5 {
                font-size: 20px !important;
                line-height: 1.3;
              }

              .articlebody iframe {
                width: 100%;
                margin: 40px auto;
                // max-height: 405px;
                min-height: 420px;
              }
              .articlebody a {
                color: #cf4520;
                border-bottom: 1px solid #cf4520;
                padding-bottom: 2px;
                cursor: pointer;
                word-break: break-word;
              }
              .articlebody a:hover {
                background: #cf4520;
                border-bottom: none;
                color: #ffffff !important;
              }
              .articlebody .isha-article-quote-bg {
                background-image: url('${config.imgPath}/d/46272/1663649433-iaq.png');
                background-position: 80% 100%;
                background-repeat: no-repeat;
                padding: 4vw 5vw 4vw;
                // margin: 2vw 3vw;
                text-align: right;
              }
              .article-right-blue {
                height: auto;
                right: 0;
                width: auto;
                background-image: url('${config.imgPath}/d/46272/1663649491-article-side-img.png');
                background-repeat: repeat-y;
                background-position-x: right;
              }

              .poemBlock {
                float: left;
                width: 100%;
                text-align: center;
                margin: 30px 0;
                padding: 50px 10px;
                padding-left: calc((100% - 750px) / 2);
                padding-right: calc((100% - 750px) / 2);
                // background-image: linear-gradient(to bottom, #f8f7ef 6%, #ffffff 99%);
                font-family: 'var(--FedraSansStd-book-i)';
                font-size: 24px;
                line-height: 1.17;
              }
              .poem-poemBlock {
                float: left;
                width: 100%;
                text-align: center;
                margin: 0 0;
                padding: 0 10px;
                padding-left: calc((100% - 750px) / 2);
                padding-right: calc((100% - 750px) / 2);
                background-color: #00334a;
              }

              .poemBlock .heading {
                float: left;
                width: 100%;
                font-family: 'var(--FedraSansStd-medium)';
                font-size: 48px;
                text-transform: uppercase;
              }
              .poem-poemBlock .heading {
                color: #faf7f0 !important;
                font-family: 'FedraSerifAStdBook' !important;
                margin: 4vw 0 0.4vw 0 !important;
                text-align: center !important;
                width: 100%;
                font-size: 45px;
                line-height: 1.38;
                letter-spacing: 1px;
                background-position: center bottom !important;
                min-height: auto !important;
                padding-bottom: 49px !important;
                background-image: url(${config.imgPath}/d/46272/1663645833-flower-divider-horizontal-new.svg);
                background-repeat: no-repeat;
              }

              .poemBlock .dividerimg {
                float: left;
                width: 100%;
                margin: 30px 0;
              }
              .poemBlock .dividerimg img {
                float: unset;
                width: auto;
              }
              .poem-poemBlock .poem {
                font-family: var(--FedraSansStd-book)-i;
                font-size: 24px;
                color: #dfd6c4;
                clear: both;
                padding-top: 5px;
              }
              .poem-poemBlock .poem p {
                margin: 20px 0;
                line-height: 1.17;
                letter-spacing: 1px;
              }
              .isha-article-caption,
              .isha-article-downloadmsg {
                color: #8a8173;
                height: auto !important;
                font-family: var(--FedraSansStd-book-i);
                font-size: 20px;
                line-height: 1.7;
                text-align: center;
                margin-bottom: 32px;
              }
              .isha-article-caption {
                display: none;
              }
              .isha-article-downloadmsg .desktop {
                display: block;
              }
              .isha-article-downloadmsg .mobile {
                display: none;
              }
              .image-shortcode {
                text-align: center;
              }
              .image-shortcode.left {
                display: block;
                height: auto;
                float: left;
                width: 100%;
              }
              .image-shortcode .isha-article-caption {
                margin: 0 auto !important;
                height: auto !important;
                margin-bottom: 40px !important;
              }
              .image-shortcode.left .isha-article-caption {
                float: left;
              }
              .divider {
                width: 100%;
                height: 2px;
                border: solid 1px #d33100;
                margin: 20px 0;
              }
              .dropcap {
                background: url('${config.imgPath}/d/46272/1663673807-invalid-name.png')
                  no-repeat;
                width: 90px;
                height: 100px;
                text-align: center;
                float: left;
                margin: 10px 10px 10px -20px;
              }
              .dropcap .text {
                margin-top: -10px !important;
                font-size: 84px;
                font-family: 'FedraSerifAStdBook';
                margin-top: -18px;
                position: absolute;
                width: 100px;
                margin-left: -48px;
              }
              .sadhguru-signature {
                min-height: 150px;
                font-family: 'var(--FedraSansStd-book)';
                font-size: 24px;
                text-align: left;
                color: #7d7c7a;
                background: url('${config.imgPath}/d/46272/1663654954-signature_name.png')
                  no-repeat;
                background-position: 0% 100%;
                margin-bottom: 80px;
                padding-top: 10px;
                letter-spacing: 1px;
                padding-bottom: 115px;
                clear: both;
              }
              .sadhguru-signature.white {
                background: url('${config.imgPath}/d/46272/1663673930-sadhguru_signature_white.svg')
                  no-repeat 0% 100%;
                color: #fff;
              }
              .sadhguru-signature.white.center {
                background: url('${config.imgPath}/d/46272/1663673930-sadhguru_signature_white.svg')
                  no-repeat center bottom;
                text-align: center;
              }
              .isha-ps {
                font-family: 'var(--FedraSansStd-book-i)';
                font-size: 18px;
                line-height: 1.61;
                color: #28231e;
              }
              .quote {
                font-family: 'var(--FedraSansStd-book)';
                font-size: 20px;
                line-height: 1.7;
                text-align: left;
                color: #28231e;
                margin-bottom: 2vw;
              }
              .toggle-box-head {
                font-family: 'var(--FedraSansStd-A-medium)' !important;
                font-size: 30px !important;
                line-height: 1.1;
                color: #28231e;
                margin-top: 20px;
                margin-bottom: 10px;
                font-weight: 300 !important;
              }
              .isha-audio-player {
                margin-top: 21px;
              }
              .audioplayer {
                display: flex;
                height: 80px;
                opacity: 0.84;
                background-color: #d8d8d8;
                padding: 0;
              }
              .audio {
                width: 720px;
                height: 80px;
                opacity: 0.9;
                color: #d8d8d8;
              }
              {/* .timeline {
                width: 75%;
                height: 2px;
                margin-top: 38px;
                float: left;
                background: #979797;
                padding: 0;
              } */}

    .timeline {
      width: 94px;
      height: 8px;
      margin-top: 38px;
      float: left;
      background: #979797;
      padding: 0;
    }

    @media (min-width: 30em) { /* sm breakpoint */
      .timeline {
        width: 94%;
      }
    }

    @media (min-width: 48em) { /* md breakpoint */
      .timeline {
        width: 70%;
      }
    }

    @media (min-width: 62em) { /* lg breakpoint */
      .timeline {
        width: 487px;
        left:15%;

      }
    }

    @media (min-width: 80em) { /* xl breakpoint */
      .timeline {
        width: 487px;
        left:15%;
      }
    }

              .mar-top30 {
                margin-top: 30px;
              }
              .tcenter {
                text-align: center;
              }
              .pButton {
                height: 80px;
                width: 60px;
                border: none;
                background-repeat: no-repeat;
                background-position: center;
                outline: none;
                font-family: 'var(--FedraSansStd-medium)';
                margin: 16px 0 0 17px;
                background: url('${config.imgPath}/d/46272/1663639558-play.svg')
                  no-repeat;
              }
              .playhead {
                width: 17px;
                height: 17px;
                border-radius: 50%;
                margin-top: -9px;
                background: #cf4520;
              }
              .isha-image-credit {
                background: url('${config.imgPath}/d/46272/1663674009-camera.svg')
                  no-repeat;
                background-position: left;
                padding-left: 28px;
                line-height: 25px;
              }
              .isha-image-credit a {
                font-family: 'var(--FedraSansStd-book-i)';
                font-size: 16px;
              }
              .introtext {
                font-family: 'var(--FedraSansStd-book-i)';
                font-size: 20px !important;
                line-height: 1.7;
                color: #28231e;
                margin-top: -10px;
              }
              .btn-isha-1 {
                font-family: 'var(--FedraSansStd-medium)';
                color: #ffffff;
                background-color: #cf4520;
                padding: 7px 12px;
                margin-bottom: 0;
                font-size: 14px;
                text-align: center;
                white-space: nowrap;
                vertical-align: middle;
                touch-action: manipulation;
                cursor: pointer;
                user-select: none;
                background-image: none;
                border: 1px solid transparent;
                border-radius: 3px;
              }
              .btn-isha-1:hover {
                background-color: #000054;
                color: #fff;
                -webkit-transition: all 500ms ease;
                -moz-transition: all 500ms ease;
                -ms-transition: all 500ms ease;
                -o-transition: all 500ms ease;
                transition: all 500ms ease;
              }
              .isha-question {
                font-family: 'var(--FedraSansStd-medium)';
              }
              .isha-answer {
                font-family: 'var(--FedraSansStd-book-i)';
                font-size: 20px;
                line-height: 1.7;
                color: #28231e;
              }
              .editor-desc {
                background: url('${config.staticPath}/assets/images/editor-note-divider.png')
                  no-repeat;
                width: 100%;
                background-position: center top;
                margin-top: 40px;
                font-size: 20px;
                line-height: 1.7;
                text-align: left;
                color: #28231e;
                font-family: 'var(--FedraSansStd-book-i)';
                text-align: center;
              }
              .editor-desc .editor-inner {
                padding-top: 40px;
                text-align: left;
              }

              .youtube-iframe {
                width: 100% !important;
                height: 100% !important;
                max-height: 100%;
              }
              .twitter-iframe iframe {
                margin: 10px auto !important;
                max-height: 605px;
              }
              @media only screen and (max-width: 736px) {
                .isha-article-quote {
                  font-size: 20px;
                }
              }
              @media only screen and (max-width: 500px) {
                :target::before {
                  content: '';
                  display: block;
                  height: 55px;
                  margin: -55px 0 0;
                }
                .articlebody table {
                  width: 100% !important;
                }
                .editor-desc .editor-inner {
                  padding: 40px 3%;
                  font-size: 15px;
                }
                iframe {
                  width: 100%;
                  min-width: 300px !important;
                  margin: 0 auto;
                  min-height: auto;
                }
                .articlebody iframe {
                  width: 100% !important;
                  height: 200px;
                  min-height: auto;
                }
                iframe #twitter-widget-0 {
                  height: 300px;
                }

                .articlebody .isha-article-quote-bg {
                  padding: 0;
                  margin: 4vw 5vw 4vw;
                }
                .isha-article-quote {
                  width: 85%;
                  margin-left: 0;
                }
                .poemBlock {
                  padding-left: 20px;
                  padding-right: 20px;
                  font-size: 16px !important;
                }
                .poem-poemBlock .poem {
                  font-size: 18px;
                }
                .poem-poemBlock .poem p {
                  margin: 0 0 15px;
                }
                .poem-poemBlock .heading {
                  margin: 4vw 0 1.4vw 0 !important;
                }
                .timeline {
                  width: 50%;
                }
                .isha-article-quote {
                  width: 84%;
                  font-size: 20px;
                  padding: 0 40px 0 0;
                  margin: 0 0 40px 40px;
                }
                .article-right-blue {
                  background-position-x: 95%;
                }
              }
              .audio-player {
                /* box-shadow: 0 0 20px 0 #000a; */
                font-family: arial;
                color: rgb(255 255 255);
                font-size: 0.75em;
                overflow: hidden;
                display: grid;
                grid-template-rows: 6px auto;
              }
            .audio-player .timeline {
      background: #979797;
      left: 85px;
      width:33%;
      position: relative;
      cursor: pointer;
      margin-top: 23px;
      /* box-shadow: 0 2px 10px 0 #0008; */
    }
    @media (min-width: 30em) { /* sm and up */
      .audio-player .timeline {
      width:50%;

      }
    }
    @media (min-width: 80em) { /* xl breakpoint */
      .audio-player .timeline {
        width: 487px; /* You can adjust this to a different value for larger screens if needed */

      }
    }
              .audio-player .timeline .progress {
                background: #60adff;
                width: 0%;
                height: 100%;
                transition: 0.25s;
              }
              .audio-player .timeline .progress:after {
                width: 20px;
                background: red;
                height: 20px;
              }
              .audio-player .controls {
                display: flex;
                justify-content: space-between;
                align-items: stretch;
                padding: 0 32px;
              }
              .audio-player .controls > * {
                display: flex;
                justify-content: center;
                align-items: center;
              }
              .audio-player .controls .toggle-play.play {
                cursor: pointer;
                position: relative;
                height: 52px;
                width: 52px;
                border: none;
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center;
                outline: none;
                font-family: 'var(--FedraSansStd-medium)';
           {/* background: url('https://static.sadhguru.org/d/46272/1726669878-frame-43.png'); */}
                  no-repeat;
              }

          .audio-player .controls .toggle-play.pause {
      position: relative;
      height: 52px;
      width: 52px;
     background-size: cover;
      border: none;
      background-repeat: no-repeat;
      background-position: center;
      outline: none;
     background-size: cover;
      font-family: 'var(--FedraSansStd-medium)';
    }

              .audio-player .controls .time {
      display: flex;
      margin-left: auto;
      color: #fefaf6;
      font-family: 'FedraSerifAStdBook';
      font-size: 1rem;
    }

    @media (min-width: 768px) {
      .audio-player .controls .time {
        margin-top: 0;
      }
    }

              .audio-player .controls .time > * {
                padding: 2px;
              }
              .audio-player .controls .volume-container {
                cursor: pointer;
                position: relative;
                z-index: 2;
              }
              .audio-player .controls .volume-container .volume-button {
                height: 26px;
                display: flex;
                align-items: center;
              }
              .audio-player .controls .volume-container .volume-button .volume {
                transform: scale(0.7);
              }
              .audio-player .controls .volume-container .volume-slider {
                position: absolute;
                left: -3px;
                top: 15px;
                z-index: -1;
                width: 0;
                height: 15px;
                background: white;
                box-shadow: 0 0 20px #000a;
                transition: 0.25s;
              }
              .audio-player
                .controls
                .volume-container
                .volume-slider
                .volume-percentage {
                background: coral;
                height: 100%;
                width: 75%;
              }
              .audio-player .controls .volume-container:hover .volume-slider {
                left: -123px;
                width: 120px;
              }
            `}
      </style>
    </Box>
  );
};

export default AudioEmbed;
